import React from 'react';
import { Event } from '../../data/eventsService';

type Props = {
    events: Event[];
    loading: boolean;
    error: string | null;
};

const cardClasses =
    'rounded-2xl border border-[#2C2731] bg-[#FFFCEF] p-6 shadow-[inset_0_0_0_1px_rgba(44,39,49,0.05)]';

const formatTimestamp = (timestamp: string): string => {
    try {
        const date = new Date(timestamp);
        return date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
        });
    } catch {
        return timestamp;
    }
};

const formatEventName = (eventName: string): string => {
    return eventName
        .split(/[-_]/)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

const getEventColor = (eventName: string): string => {
    if (eventName.includes('match')) return 'bg-[#D6E9F7] text-[#2C5F8D] border-[#B3D8F0]';
    if (eventName.includes('session') || eventName.includes('consultation')) return 'bg-[#E0F2D6] text-[#4A6B3E] border-[#C5E5B5]';
    if (eventName.includes('account') || eventName.includes('onboarding')) return 'bg-[#EAE0F7] text-[#5C4A7A] border-[#D5C5EA]';
    if (eventName.includes('reject') || eventName.includes('no_show')) return 'bg-[#FCEDEA] text-[#B24E4E] border-[#F5D0C8]';
    return 'bg-[#F2E0A8] text-[#403851] border-[#E6D495]';
};

const MetadataDisplay: React.FC<{ metadata: Record<string, any> }> = ({ metadata }) => {
    const filteredMetadata = Object.entries(metadata).filter(
        ([key]) => !['seeker_id', 'user_id', 'timestamp'].includes(key)
    );

    if (filteredMetadata.length === 0) return null;

    return (
        <div className="mt-2 space-y-1">
            {filteredMetadata.map(([key, value]) => {
                const displayValue = typeof value === 'object'
                    ? JSON.stringify(value)
                    : String(value);

                if (displayValue.length > 100) return null;

                return (
                    <div key={key} className="flex gap-2 text-xs">
                        <span className="font-medium text-[#403851]/80">
                            {key.replace(/_/g, ' ')}:
                        </span>
                        <span className="text-[#2C2731]">{displayValue}</span>
                    </div>
                );
            })}
        </div>
    );
};

export default function SeekerEventsPanel({ events, loading, error }: Props) {
    if (loading) {
        return (
            <div className={cardClasses}>
                <h3 className="mb-4 text-lg font-semibold text-[#2C2731]">Events</h3>
                <div className="animate-pulse space-y-4">
                    <div className="h-4 w-1/4 rounded bg-[#F2E0A8]"></div>
                    <div className="space-y-3">
                        <div className="h-4 rounded bg-[#F2E0A8]"></div>
                        <div className="h-4 w-3/4 rounded bg-[#F6E5B7]"></div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={cardClasses}>
                <h3 className="mb-4 text-lg font-semibold text-[#2C2731]">Events</h3>
                <div className="rounded-xl border border-[#B24E4E]/40 bg-[#FCEDEA] px-3 py-2 text-sm text-[#B24E4E]">
                    {error}
                </div>
            </div>
        );
    }

    if (events.length === 0) {
        return (
            <div className={cardClasses}>
                <h3 className="mb-4 text-lg font-semibold text-[#2C2731]">Events</h3>
                <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-[#2C2731]/20 bg-[#FDF1C3] px-6 py-10 text-center">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full border border-[#2C2731]/10 bg-[#F2E0A8]">
                        <svg className="h-6 w-6 text-[#403851]/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                        </svg>
                    </div>
                    <div>
                        <h4 className="text-base font-semibold text-[#2C2731]">No events yet</h4>
                        <p className="text-sm text-[#403851]">
                            Events will appear here as the seeker interacts with the platform.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`${cardClasses} space-y-4`}>
            <div className="flex items-start justify-between gap-4 border-b border-[#2C2731]/10 pb-4">
                <h3 className="text-lg font-semibold text-[#2C2731]">Events</h3>
                <span className="text-xs font-medium uppercase tracking-[0.08em] text-[#403851]/80">
                    {events.length} event{events.length !== 1 ? 's' : ''}
                </span>
            </div>

            <div className="space-y-3 max-h-96 overflow-y-auto">
                {events.map((event) => (
                    <div
                        key={event.id}
                        className="rounded-2xl border border-[#2C2731]/10 bg-[#FFFCEF] p-4 shadow-[inset_0_0_0_1px_rgba(44,39,49,0.02)]"
                    >
                        <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 space-y-2">
                                <div className="flex items-center gap-2">
                                    <span
                                        className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${getEventColor(
                                            event.event_name
                                        )}`}
                                    >
                                        {formatEventName(event.event_name)}
                                    </span>
                                </div>
                                <MetadataDisplay metadata={event.metadata || {}} />
                            </div>
                            <div className="flex-shrink-0 text-right">
                                <p className="text-xs font-medium uppercase tracking-[0.08em] text-[#403851]/80">
                                    {formatTimestamp(event.timestamp || event.created_at)}
                                </p>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
